import cn from 'classnames';

import { useCheckoutContext, Article, ArticleBasedVoucher, Price } from '@zooplus/checkout-state';
import { P1, Anchor } from '@zooplus/ui-components';

import CartItemQuantityHandler from '../cart-item-quantity-handler';
import styles from '../cart-item.module.scss';
import CartSubscriptionToggle from '../cart-subscription-toggle';
import ItemStandardPrice from '../item-standard-price';

import ItemBasedDiscount from '@components/common/item-based-discount';
import PriceWrapper from '@components/common/price-wrapper/price-wrapper';
import {
  CART_ITEM_IMAGE_DIMENSION,
  CART_VERSION,
  CHECKOUT_STATE_PROJECT_ID,
} from '@utilities/constants';
import { showFallbackImage } from '@utilities/fallback-image';
import { isObjectNotNullOrEmpty, trimLeadingHyphenAndSpaces } from '@utilities/utils';

const StandardItem = (article: Article) => {
  const { cartVersion, mainTogglesMap = {} } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
  const isNewPricingEnabled = mainTogglesMap.NEW_PRICING_COMPONENT_IN_CART_AND_PREVIEW;

  const autoshipmentDetails = isCartApiV2
    ? article.lineItemDiscounts?.autoshipmentDiscount
    : article.subscriptionDetails;
  const autoshipmentApplied = isCartApiV2
    ? article.lineItemDiscounts?.autoshipmentDiscount?.applied
    : article.subscriptionApplied;
  const abdApplied = isCartApiV2
    ? article.lineItemDiscounts?.articleBasedDiscount?.applied
    : article.voucherApplied;
  const isPersonalized = isCartApiV2
    ? article.lineItemDiscounts?.articleBasedDiscount?.isPersonalized
    : article.articleBasedVoucher?.isPersonalized;
  const voucherLabel = isCartApiV2
    ? article.lineItemDiscounts?.articleBasedDiscount?.label
    : article.articleBasedVoucher?.voucherLabel;
  const abdDetails = { isPersonalized, voucherLabel };
  const productLink = `${article.productLink}?activeVariant=${article.shopId}`;
  const availableOffers = article.availableOffers;
  const { WIDTH, HEIGHT } = CART_ITEM_IMAGE_DIMENSION;

  return (
    <section className={styles.productInfoWrapper}>
      <div className={styles.productImgWrapper}>
        <Anchor href={productLink} size="small">
          <img
            loading="lazy"
            src={article.pictureUrl}
            alt={article.name}
            onError={(e) =>
              showFallbackImage(e, {
                width: WIDTH,
                height: HEIGHT,
              })
            }
            width={WIDTH}
            height={HEIGHT}
          />
        </Anchor>
      </div>

      <article data-testid="product-info" className={styles.productNameWrapper}>
        <Anchor data-testid="product-name" href={productLink} isBold>
          {article.name}
        </Anchor>
        <P1 data-testid="product-variant" className={styles.productVariantName}>
          {trimLeadingHyphenAndSpaces(article.variantName)}
        </P1>
        {abdApplied && isObjectNotNullOrEmpty(abdDetails) && (
          <ItemBasedDiscount {...(abdDetails as ArticleBasedVoucher)} />
        )}
      </article>

      <div className={styles.productPricingDetails}>
        <article className={styles.priceDetailsWrapper}>
          <div className={styles.innerWrapper}>
            <div className={cn(styles.priceWrapper, isNewPricingEnabled && styles.newPriceWrapper)}>
              <PriceWrapper article={article} />
            </div>
            <CartItemQuantityHandler {...article} />
            <div className={styles.productValueAsPerQuantity}>
              <ItemStandardPrice
                formattedSubtotal={article.formattedSubtotal}
                isNewPricingEnabled={isNewPricingEnabled}
              />
            </div>
          </div>
        </article>
      </div>

      {autoshipmentDetails?.displaySwitch && (
        <CartSubscriptionToggle
          id={article.id}
          articleQuantity={article.quantity}
          autoshipmentApplied={autoshipmentApplied}
          autoshipmentDetails={autoshipmentDetails}
          shopIdentifier={article.shopId}
          subtotal={article.subtotal}
          actualPrice={
            (isCartApiV2 ? (article?.price as Price)?.value : article?.actualPrice) as number
          }
          abdApplied={abdApplied}
          abdOfferId={
            abdApplied
              ? availableOffers &&
                availableOffers?.find((offer) => offer?.offerType?.includes('ABD'))?.offerId
              : undefined
          }
          offerId={
            availableOffers?.find(
              (offer) => !(offer?.offerType?.includes('AUS') || offer?.offerType?.includes('ABD')),
            )?.offerId
          }
          ausOfferId={
            availableOffers &&
            availableOffers?.find((offer) => offer?.offerType?.includes('AUS'))?.offerId
          }
        />
      )}
    </section>
  );
};

export default StandardItem;
